import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useAuth } from '../../../auth/hooks/useAuth';
import { useTickerDirectorySearch } from '../../hooks/useTickerDirectorySearch';
import {
  searchTickerDirectory,
  type TickerDirectorySearchResult,
} from '../../services/tickerApi';

export interface TickerAutocompleteProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  hasError?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

const INPUT_CLASS =
  'block w-full uppercase bg-transparent px-3 py-2 border-0 focus:outline-none focus:ring-0 sm:text-sm';

const WRAPPER_CLASS =
  'relative rounded-md shadow-sm border focus-within:ring-1 focus-within:ring-green-500 focus-within:border-green-500';

function findExactCompanyName(
  results: TickerDirectorySearchResult[],
  ticker: string
): string | null {
  const match = results.find((row) => row.ticker.toUpperCase() === ticker);
  return match?.company_name ?? null;
}

const TickerAutocomplete: React.FC<TickerAutocompleteProps> = ({
  id,
  value,
  onChange,
  onBlur,
  hasError = false,
  placeholder = 'e.g., AAPL',
  disabled = false,
}) => {
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { session } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string | null>(null);

  const trimmedValue = value.trim();
  const { data: suggestions = [], isFetching } = useTickerDirectorySearch(value);
  const showDropdown =
    isOpen && trimmedValue.length >= 1 && (isFetching || suggestions.length > 0);
  const showSelectedLabel =
    !!selectedCompanyName && !isOpen && !isFocused && trimmedValue.length > 0;

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [suggestions]);

  useEffect(() => {
    if (!trimmedValue) {
      setSelectedCompanyName(null);
    }
  }, [trimmedValue]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const selectSuggestion = useCallback(
    (ticker: string, companyName: string | null) => {
      onChange(ticker);
      setSelectedCompanyName(companyName);
      setIsOpen(false);
      setHighlightedIndex(-1);
      // Blur so the ticker + gray company name display appears and parent blur handlers run.
      queueMicrotask(() => inputRef.current?.blur());
    },
    [onChange]
  );

  /** On blur, show the company name when the typed ticker exactly matches the directory. */
  const resolveCompanyNameOnBlur = useCallback(async () => {
    const ticker = value.trim().toUpperCase();
    if (!ticker) return;

    const fromSuggestions = findExactCompanyName(suggestions, ticker);
    if (fromSuggestions) {
      setSelectedCompanyName(fromSuggestions);
      return;
    }

    if (!session?.access_token) return;

    try {
      const results = await searchTickerDirectory({
        query: ticker,
        limit: 10,
        token: session.access_token,
      });
      const name = findExactCompanyName(results, ticker);
      // Ignore stale responses if the user changed the field while the request was in flight.
      if (name && value.trim().toUpperCase() === ticker) {
        setSelectedCompanyName(name);
      }
    } catch {
      // Unknown ticker — leave the field ticker-only.
    }
  }, [value, suggestions, session?.access_token]);

  const handleInputBlur = useCallback(() => {
    setIsFocused(false);
    setIsOpen(false);
    onBlur?.();
    void resolveCompanyNameOnBlur();
  }, [onBlur, resolveCompanyNameOnBlur]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || suggestions.length === 0) {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
        break;
      case 'ArrowUp':
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev <= 0 ? suggestions.length - 1 : prev - 1
        );
        break;
      case 'Enter':
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          event.preventDefault();
          const item = suggestions[highlightedIndex];
          selectSuggestion(item.ticker, item.company_name);
        }
        break;
      case 'Escape':
        event.preventDefault();
        setIsOpen(false);
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <div
        className={`${WRAPPER_CLASS} ${hasError ? 'border-red-300' : 'border-gray-300'}`}
      >
        {showSelectedLabel && (
          <div
            className="pointer-events-none absolute inset-0 flex items-center gap-2 overflow-hidden px-3 py-2 sm:text-sm"
            aria-hidden="true"
          >
            <span className="shrink-0 uppercase text-gray-900">{value}</span>
            <span className="truncate normal-case text-gray-400">{selectedCompanyName}</span>
          </div>
        )}

        <input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            highlightedIndex >= 0 ? `${listboxId}-option-${highlightedIndex}` : undefined
          }
          autoComplete="off"
          spellCheck={false}
          disabled={disabled}
          value={value}
          placeholder={placeholder}
          className={`${INPUT_CLASS} ${showSelectedLabel ? 'text-transparent caret-gray-900' : ''}`}
          onFocus={() => {
            setIsFocused(true);
            setIsOpen(true);
          }}
          onBlur={handleInputBlur}
          onChange={(event) => {
            setSelectedCompanyName(null);
            onChange(event.target.value.toUpperCase());
          }}
          onKeyDown={handleKeyDown}
        />
      </div>

      {showDropdown && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
        >
          {isFetching && suggestions.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-400">Searching…</li>
          ) : (
            suggestions.map((item, index) => {
              const isHighlighted = index === highlightedIndex;
              return (
                <li
                  key={item.ticker}
                  id={`${listboxId}-option-${index}`}
                  role="option"
                  aria-selected={isHighlighted}
                  className={`cursor-pointer px-3 py-2 text-sm ${
                    isHighlighted ? 'bg-green-50 text-gray-900' : 'text-gray-900 hover:bg-gray-50'
                  }`}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => selectSuggestion(item.ticker, item.company_name)}
                >
                  <span className="font-medium">{item.ticker}</span>
                  {item.company_name ? (
                    <span className="ml-2 text-gray-400">{item.company_name}</span>
                  ) : null}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
};

export default TickerAutocomplete;
